// Block subsequent calls to an async function until the previous call has completed.
// Throws an error if you call the function too soon.
export default function blockCallsUntilComplete(fn) {
  let blocked = false;
  return async function blocker(...args) {
    if (blocked) {
      throw new Error('The previous call to this function has not yet completed.');
    }
    blocked = true;
    try {
      return await fn.call(this, ...args);
    } finally {
      blocked = false;
    }
  };
}

function callSafely(fn, context, args) {
  try {
    return Promise.resolve(fn.apply(context, args));
  } catch (err) {
    return Promise.reject(err);
  }
}

// Like blockCallsUntilComplete, but instead of throwing when called while busy,
// it queues at most one pending call. Whether a call gets queued or skipped
// is decided purely by its first (boolean) argument — if it matches the flag
// of the call currently in flight, this call is redundant and just resolves
// with that call's result. Any other arguments are only used once a call
// actually runs; they play no part in the queue/skip decision.
export function queueLatestCall(fn) {
  let current = null; // promise of the call currently executing, or null when idle
  let runningFlag = null; // boolean flag of the call currently executing
  let queued = null; // the one call waiting its turn: { context, args, resolve, reject, promise }

  function run(context, args) {
    runningFlag = args[0];
    // eslint-disable-next-line no-use-before-define
    current = callSafely(fn, context, args).finally(settle);
    return current;
  }

  function settle() {
    if (!queued) {
      current = null;
      runningFlag = null;
      return;
    }

    const { context, args, resolve, reject } = queued;
    queued = null;
    run(context, args).then(resolve, reject);
  }

  return function (flag, ...rest) {
    const context = this;
    const args = [flag, ...rest];

    if (!current) {
      return run(context, args);
    }

    if (flag === runningFlag) {
      // Same flag as the call already in flight — anything queued behind
      // it is now moot, since we'd just end up back at this same state.
      if (queued) {
        current.then(queued.resolve, queued.reject);
        queued = null;
      }
      return current;
    }

    // Only the most recent pending call matters, so replace whatever was
    // waiting before it — this is also what keeps its `rest` args current.
    if (queued) {
      queued.context = context;
      queued.args = args;
      return queued.promise;
    }

    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    queued = { context, args, resolve, reject, promise };
    return promise;
  };
}
